import * as React from 'react';
import { observer } from 'mobx-react';
import { PageHeader } from './page-header';
import {em, important, percent, px, url, viewWidth} from 'csx';
import {mediaTablet, mediaMobileOnly, mediaDesktop} from '../theme/media';
import {classes, style} from 'typestyle';
import { LoadingContainer } from './loading-container';
import {
  aroundJustified,
  betweenJustified,
  center,
  centerCenter,
  centerJustified,
  end,
  horizontal,
  horizontallySpaced,
  start,
  startJustified,
  vertical,
  verticallySpaced,
  wrap
} from 'csstips';
import { colorBackground, colorBrand, colorSlate } from '../theme/color';
import { fontBody, fontHeading } from '../theme/font';
import { PageHomeCTASeparator } from './page-home-cta-separator';
import { ButtonPill } from './button-pill';
import { TextNoOrphan } from './text-no-orphan';
import { HookdDefinition } from './hookd-definition';
import { ImageTile } from './image-tile';
import { constantGoldenRatio } from '../theme/constant';
import {ResponsiveImageTile} from './responsive-image-tile';
import {ImagePreloader} from '../types/fn';
import {ResponsiveImage} from './responsive-image';

export interface PageAboutModel {
  loading: boolean;
  page_title: string;
  page_subtitle: string;
  cover_image: string;
  definition_title: string;
  definition_content: string;
  section_1_title: string;
  section_1_content: string;
  section_1_image: string;
  section_2_title: string;
  section_2_content: string;
  section_2_image: string;
  section_3_title: string;
  section_3_content: string;
  section_3_image: string;
  quote_content: string;
  quote_by: string;
  cta_title: string;
  cta_content: string;
  cta_button: string;
  team_title: string;
  team_strap: string;
  team_members: Array<{
    avatar: string;
    name: string;
    role: string;
  }>;
}

export interface PageAboutProps {
  model: PageAboutModel;
  preload: ImagePreloader;
  onSignUp?(): void;
}

const constants = {
  desktopMaxWidth: px(600)
}

@observer
export class PageAbout extends React.Component<PageAboutProps, any> {
  render() {
    const { model, onSignUp, preload } = this.props;
    
    return (
      <LoadingContainer loading={model.loading}>
        {!model.loading && (
          <PageHeader coverImage={model.cover_image}>
            <h1>
              <TextNoOrphan value={model.page_title} />
            </h1>
            <p>
              <TextNoOrphan value={model.page_subtitle} />
            </p>
          </PageHeader>
        )}
        {!model.loading && (
          <div className={PageAbout.styles.content}>
            <div className={PageAbout.styles.sections}>
              <div className={PageAbout.styles.section}>
                <div>
                  <h2>{model.definition_title}</h2>
                  <p>{model.definition_content}</p>
                </div>
                <div>
                  <HookdDefinition
                    className={PageAbout.styles.definition}
                  />
                </div>
              </div>
              <div className={PageAbout.styles.section}>
                <div>
                  <ResponsiveImageTile
                    className={PageAbout.styles.sectionImage}
                    identity={model.section_1_image}
                    preload={preload}
                    ratio={constantGoldenRatio}
                  />
                </div>
                <div>
                  <h3>{model.section_1_title}</h3>
                  <p>{model.section_1_content}</p>
                </div>
              </div>
              <div className={PageAbout.styles.section}>
                <div>
                  <h3>{model.section_2_title}</h3>
                  <p>{model.section_2_content}</p>
                </div>
                <div>
                  <ResponsiveImageTile
                    className={PageAbout.styles.sectionImage}
                    identity={model.section_2_image}
                    preload={preload}
                    ratio={constantGoldenRatio}
                  />
                </div>
              </div>
              <div className={PageAbout.styles.section}>
                <div>
                  <ResponsiveImageTile
                    className={PageAbout.styles.sectionImage}
                    identity={model.section_3_image}
                    ratio={constantGoldenRatio}
                    preload={preload}
                  />
                </div>
                <div>
                  <h3>{model.section_3_title}</h3>
                  <p>{model.section_3_content}</p>
                </div>
              </div>
            </div>
            <div className={PageAbout.styles.quote}>
              <div />
              <div>“</div>
              <div>{model.quote_content}</div>
              <div>{model.quote_by}</div>
              <div />
            </div>
            <PageHomeCTASeparator className={PageAbout.styles.ctaSeparator}/>
            <div className={PageAbout.styles.cta}>
              <h3>{model.cta_title}</h3>
              <p>{model.cta_content}</p>
              {onSignUp && (
                <ButtonPill onClick={onSignUp} primary={true}>
                  {model.cta_button}
                </ButtonPill>
              )}
            </div>
          </div>
        )}
      </LoadingContainer>
    )
  }
  
  static styles = {
    content: style({
      padding: px(20)
    }),
    definition: style({
      boxShadow: '5px 5px 15px 0 rgba(0, 0, 0, 0.11)'
    }),
    sections: style(
      mediaMobileOnly({
        ...vertical,
        ...verticallySpaced(40),
        margin: '0 0 2rem 0',
        $nest: {
          '&> div:first-child h2': {
            marginTop: 0
          }
        }
      }),
      mediaTablet({
        ...vertical,
        ...verticallySpaced(60),
        margin: '2rem auto',
        maxWidth: px(1280),
      })
    ),
    sectionImage: style(
      mediaTablet({
        width: percent(100),
        maxWidth: constants.desktopMaxWidth
      }),
      mediaMobileOnly({
        width: percent(100),
        maxWidth: percent(100)
      })
    ),
    section: style(
      mediaMobileOnly({
        ...vertical,
        $nest: {
          '&:nth-child(even)': {
            flexDirection: 'column-reverse'
          },
          '& h3': {
            color: colorBrand.toString(),
            fontSize: px(30),
            margin: 0,
          },
          '& h2': {
            color: colorBrand.toString(),
            fontSize: px(40),
            margin: 0,
          },
          '& h4': {
            color: colorSlate.toString(),
            fontSize: px(18),
            margin: 0,
          }
        }
      }),
      mediaTablet({
        ...horizontal,
        ...center,
        $nest: {
          '&> div': {
            ...vertical,
            ...verticallySpaced(20),
            ...end,
            ...centerJustified,
            flex: 1
          },
          '&> div:last-child': {
            ...start,
          },
          '& h3': {
            color: colorBrand.toString(),
            fontSize: px(30),
            maxWidth: constants.desktopMaxWidth,
            width: percent(100),
            margin: 0,
            padding: '0 2rem',
          },
          '& h2': {
            color: colorBrand.toString(),
            fontSize: px(40),
            maxWidth: constants.desktopMaxWidth,
            width: percent(100),
            margin: 0,
            padding: '0 2rem',
          },
          '& h4': {
            color: colorSlate.toString(),
            fontSize: px(18),
            maxWidth: constants.desktopMaxWidth,
            width: percent(100),
            margin: '0 0 1rem 0',
            padding: '0 2rem',
          },
          '& p': {
            maxWidth: constants.desktopMaxWidth,
            margin: 0,
            padding: '0 2rem',
          }
        }
      }),
    ),
    team: style(
      mediaMobileOnly({
        $nest: {
          '& h2': {
            color: colorBrand.toString(),
            fontSize: px(40)
          },
          '&> div': {
            ...horizontal,
            ...wrap,
            ...aroundJustified,
            margin: '1.5rem -1rem 0 0',
          }
        }
      }),
      mediaTablet({
        padding: '0 2rem 0 3rem',
        maxWidth: px(1280),
        margin: '3rem auto',
        $nest: {
          '& h2': {
            color: colorBrand.toString(),
            fontSize: px(40)
          },
          '&> div': {
            ...horizontal,
            ...wrap,
            ...aroundJustified,
            margin: '1.5rem -1rem 0 0',
          },
          '&> p': {
            minWidth: px(400),
            width: viewWidth(35)
          }
        }
      })
    ),
    member: style(
      mediaMobileOnly({
        ...vertical,
        ...verticallySpaced(20),
        flex: '1 0 auto',
        margin: '0 1rem 2rem 0',
        width: `calc((100% / 2) - 2rem)`,
        maxWidth: `calc((100% / 2) - 2rem)`,
        $nest: {
          '&> div:first-child': {
            width: viewWidth(35),
            maxWidth: viewWidth(35),
            height: viewWidth(35),
            maxHeight: viewWidth(35),
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            borderRadius: percent(50)
          },
          '& h5': {
            margin: 0,
            fontSize: px(16),
          },
          '& p': {
            margin: '4px 0',
            fontSize: px(14),
            lineHeight: em(1.25)
          }
        }
      }),
      mediaTablet({
        ...vertical,
        ...verticallySpaced(20),
        flex: '1 0 auto',
        margin: '0 1rem 2rem 0',
        width: `calc((100% / 3) - 2rem)`,
        maxWidth: `calc((100% / 3) - 2rem)`,
        $nest: {
          '&> div:first-child': {
            width: viewWidth(20),
            maxWidth: viewWidth(20),
            height: viewWidth(20),
            maxHeight: viewWidth(20),
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            borderRadius: percent(50)
          },
          '& h5': {
            margin: 0,
            fontSize: px(16),
          },
          '& p': {
            margin: '4px 0',
            fontSize: px(14),
            lineHeight: em(1.25)
          }
        }
      }),
    ),
    memberExtra: style(
      mediaDesktop({
        width: important(`calc((100% / 5) - 2rem)`),
        maxWidth: important(`calc((100% / 5) - 2rem)`),
        $nest: {
          '&> div:first-child': {
            width: important('12vw'),
            maxWidth: important('180px'),
            height: important('12vw'),
            maxHeight: important('180px'),
          }
        }
      })
    ),
    quote: style(
      mediaMobileOnly({
        ...centerCenter,
        ...vertical,
        margin: '2rem auto',
        $nest: {
          '&> div:nth-child(1)': {
            height: px(48),
            borderLeft: `1px solid ${colorBackground.toString()}`
          },
          '&> div:nth-child(2)': {
            fontFamily: fontHeading,
            fontSize: px(100),
            color: colorBrand.toString(),
            padding: '2rem 0 0 0',
            margin: '0 0 -0.5rem 0'
          },
          '&> div:nth-child(3)': {
            fontSize: px(30),
            lineHeight: px(36),
            maxWidth: px(640),
            fontWeight: 300,
            textAlign: 'center'
          },
          '&> div:nth-child(4)': {
            fontFamily: fontHeading,
            fontSize: px(20),
            lineHeight: px(46),
          },
          '&> div:nth-child(5)': {
            height: px(48),
            borderLeft: `1px solid ${colorBackground.toString()}`
          }
        }
      }),
      mediaTablet({
        ...centerCenter,
        ...vertical,
        margin: '2rem auto',
        $nest: {
          '&> div:nth-child(1)': {
            height: px(48),
            borderLeft: `1px solid ${colorBackground.toString()}`
          },
          '&> div:nth-child(2)': {
            fontFamily: fontHeading,
            fontSize: px(100),
            color: colorBrand.toString(),
            padding: '2rem 0 0 0',
            margin: '0 0 -0.5rem 0'
          },
          '&> div:nth-child(3)': {
            fontSize: px(30),
            lineHeight: px(36),
            maxWidth: px(640),
            fontWeight: 300,
            textAlign: 'center'
          },
          '&> div:nth-child(4)': {
            fontFamily: fontHeading,
            fontSize: px(20),
            lineHeight: px(46),
          },
          '&> div:nth-child(5)': {
            height: px(48),
            borderLeft: `1px solid ${colorBackground.toString()}`
          }
        }
      }),
    ),
    ctaSeparator: style(
      mediaMobileOnly({
        display: 'none'
      })
    ),
    cta: style({
      ...centerCenter,
      ...vertical,
      ...verticallySpaced(30),
      margin: '2rem auto 3rem',
      $nest: {
        '& h3': {
          color: colorBrand.toString(),
          fontSize: px(40),
          lineHeight: px(46),
          maxWidth: px(400),
          margin: 0,
          textAlign: 'center'
        },
        '& p': {
          margin: 0,
        },
        '& [role="button"]': {
          fontSize: px(20),
          fontWeight: 'bold'
        }
      }
    })
  };
}